import React, { useState, useRef } from 'react';
import type { ModuleId, CubeType } from '../types';

// 4 interactive side faces per cube — HORIZONTAL (Y-axis) ROTATION ONLY.
// Labels lie flat on each face: when a face rotates to front it is
// automatically upright and readable. No counter-rotation needed.
const commandFaces = [
  { id: 'dashboard' as ModuleId, label: 'DASHBOARD', className: 'face-front' },
  { id: 'investor' as ModuleId, label: 'INVESTOR', className: 'face-right' },
  { id: 'vault' as ModuleId, label: 'VAULT', className: 'face-back' },
  { id: 'funnels' as ModuleId, label: 'FUNNELS', className: 'face-left' },
];

const legacyFaces = [
  { id: 'brittany' as ModuleId, label: 'BRITTANY\'S CORNER', className: 'face-front' },
  { id: 'family-vault' as ModuleId, label: 'FAMILY VAULT', className: 'face-right' },
  { id: 'legacy-projects' as ModuleId, label: 'LEGACY PROJECTS', className: 'face-back' },
  { id: 'education-center' as ModuleId, label: 'EDUCATION', className: 'face-left' },
];

const growthFaces = [
  { id: 'search' as ModuleId, label: 'SEARCH', className: 'face-front' },
  { id: 'product-builder' as ModuleId, label: 'PRODUCT BUILDER', className: 'face-right' },
  { id: 'affiliate-tools' as ModuleId, label: 'AFFILIATE TOOLS', className: 'face-back' },
  { id: 'analytics' as ModuleId, label: 'ANALYTICS', className: 'face-left' },
];

const cubeConfigs = {
  command: commandFaces,
  legacy: legacyFaces,
  growth: growthFaces,
};

const moduleColors: Record<ModuleId, string> = {
  // Command Cube
  dashboard: '#3A7BFF',
  'opportunity-radar': '#3A7BFF',
  control: '#FF8F00',
  vault: '#9E9E9E',
  ai: '#8A2BE2',
  investor: '#B71C1C',
  funnels: '#00C853',
  // Legacy Cube
  brittany: '#00C853',
  'legacy-projects': '#795548',
  'family-vault': '#607D8B',
  'education-center': '#FF9800',
  'creator-studio': '#E91E63',
  'future-planning': '#9C27B0',
  // Growth Cube
  search: '#00E5FF',
  'affiliate-tools': '#4CAF50',
  'product-builder': '#2196F3',
  analytics: '#FF5722',
  campaigns: '#FFC107',
  'marketing-systems': '#00BCD4',
};

interface CubeProps {
  onSelect: (moduleId: ModuleId) => void;
  mode: 'standby' | 'active';
  cubeType: CubeType;
}

export default function Cube({ onSelect, mode = 'standby', cubeType }: CubeProps) {
  const [rotation, setRotation] = useState({ y: 30 });
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number } | null>(null);
  const movedDistance = useRef(0);

  const faces = cubeConfigs[cubeType];

  const handleStart = (e: any) => {
    if (mode !== 'standby') return;
    const point = e.touches ? e.touches[0] : e;
    start.current = { x: point.clientX };
    movedDistance.current = 0;
    setDragging(true);
  };

  const handleMove = (e: any) => {
    if (!start.current || mode !== 'standby') return;
    const point = e.touches ? e.touches[0] : e;

    // CRITICAL: Only allow horizontal (Y-axis) rotation
    const deltaX = point.clientX - start.current.x;
    movedDistance.current += Math.abs(deltaX);

    // Surface follows the finger/cursor: drag right -> cube surface moves right
    setRotation(prev => ({ y: prev.y + deltaX * 0.35 }));
    start.current = { x: point.clientX };
  };

  const handleEnd = () => {
    if (!start.current) return;
    start.current = null;
    setDragging(false);
    // Snap to the nearest face angle (+30° presentation offset) for a clean settle
    setRotation(prev => {
      const base = Math.round((prev.y - 30) / 90) * 90 + 30;
      return { y: base };
    });
  };

  const handleFaceClick = (id: ModuleId) => {
    // Ignore clicks that were actually drags
    if (movedDistance.current > 8) return;
    onSelect(id);
  };

  const resetView = () => {
    setRotation({ y: 30 });
  };

  return (
    <div
      className="scene"
      data-testid="command-cube-scene"
      onMouseDown={mode === 'standby' ? handleStart : undefined}
      onMouseMove={mode === 'standby' ? handleMove : undefined}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={mode === 'standby' ? handleStart : undefined}
      onTouchMove={mode === 'standby' ? handleMove : undefined}
      onTouchEnd={handleEnd}
    >
      <div
        className="cube"
        data-testid="command-cube"
        style={{
          transform: `rotateX(-14deg) rotateY(${rotation.y}deg)`,
          transition: dragging ? 'none' : 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
          opacity: mode === 'standby' ? 1 : 0.2,
          pointerEvents: mode === 'standby' ? 'auto' : 'none',
        }}
      >
        {faces.map(face => (
          <div
            key={face.id}
            className={`face ${face.className}`}
            data-testid={`cube-face-${face.id}`}
            onClick={() => handleFaceClick(face.id)}
          >
            <div
              className="label"
              style={{
                color: moduleColors[face.id],
                textShadow: `0 0 10px ${moduleColors[face.id]}80`,
              }}
            >
              {face.label}
            </div>
          </div>
        ))}
        <div className="face face-cap face-top" aria-hidden="true" />
        <div className="face face-cap face-bottom" aria-hidden="true" />
      </div>

      {mode === 'standby' && (
        <button className="cube-reset-btn" data-testid="cube-reset-view-btn" onClick={resetView}>
          ↻ Reset View
        </button>
      )}
    </div>
  );
}
